import React from "react"

import { DateTime } from "@bounce/date"
import { Trans } from "@bounce/i18n"
import type { Nullable } from "@bounce/util"
import { Tag, PNImage, PostAuthor, isFullPostAuthor } from "@bounce/web-components"

import { Breadcrumbs } from "@/components/Generic/Breadcrumbs/Breadcrumbs"
import type { FormattedBlogPost } from "@/libs/prismic/types"
import type { Breadcrumb } from "@/types"

import DEFAULT_ARTICLE_IMAGE from "@/assets/img/default_hero_2.png"

export type HeaderProps = {
  breadcrumbs: Breadcrumb[]
  article: FormattedBlogPost
}

const getPublicationDate = (date?: Nullable<string>) => {
  if (!date) return

  const datetime = DateTime.fromISO(date)
  const humanFormat = datetime.toLocaleStringGlobal(DateTime.DATE_MED)
  const isoFormat = datetime.toISODate()

  return {
    humanFormat,
    isoFormat,
  }
}

export const Header = ({ breadcrumbs, article }: HeaderProps) => {
  const { image, title, tags, author } = article
  const hasAuthor = isFullPostAuthor(author)
  const date = getPublicationDate(article.dateModified ?? article.datePublished)

  return (
    <header className="flex w-full flex-col pt-10 md:pt-12">
      <Breadcrumbs breadcrumbs={breadcrumbs} className="mb-6" />
      {/* Tags */}
      {!!tags.length && (
        <div className="mb-4 flex w-full flex-row items-center gap-2">
          {tags.map(t => (
            <Tag key={t} color="grey" text={t} />
          ))}
        </div>
      )}
      {/* Title */}
      <h1 className="title2">{title}</h1>
      {/* Metadata */}
      <div className="mt-4 flex flex-col items-start justify-between gap-2 md:mt-8 md:flex-row md:items-center">
        {!!hasAuthor && <PostAuthor {...author} imageLoading="eager" />}
        {date && (
          <span className="text-sm text-neutral-800" data-chromatic="ignore">
            <Trans
              ns="marketing-generic"
              values={{ date: date.humanFormat }}
              i18nKey="cmp.article.postedDateV2"
              defaults="Posted <time>{{date}}</time>"
              components={{
                time: <time dateTime={date.isoFormat} data-chromatic="ignore" suppressHydrationWarning />,
              }}
            />
          </span>
        )}
      </div>
      <PNImage
        src={image ?? DEFAULT_ARTICLE_IMAGE}
        width={900}
        height={400}
        imgixParams={{ w: 900, fit: "crop", ar: "3:2" }}
        loading="eager"
        priority
        className="mt-4 w-full overflow-hidden object-contain object-left-top"
      />
    </header>
  )
}
